import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BarChart3, TrendingUp, AlertTriangle, PieChart, CheckCircle2 } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const features = [
  { icon: TrendingUp, title: "Predictive Pipeline Forecasting", description: "AI-powered revenue predictions based on real-time deal data and historical patterns." },
  { icon: BarChart3, title: "AI-Powered Conversion Modeling", description: "Understand which deals are most likely to close and why." },
  { icon: AlertTriangle, title: "Deal Risk Identification", description: "Get early warnings on deals at risk before they slip." },
  { icon: PieChart, title: "Revenue Dashboards", description: "Visual insights into pipeline health, velocity, and team performance." },
];

export default function AIRevenueIntelligence() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Aiveliq360 Revenue Intelligence | Predictive Sales Forecasting AI"
        description="Forecast pipeline health, identify deal risks, and unlock real-time revenue insights with Aiveliq360's AI Revenue Intelligence and predictive analytics engine."
      />

      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/auth"><Button size="sm" className="gap-1.5">Get Started<ArrowRight className="h-3.5 w-3.5" /></Button></Link>
          </div>
        </div>
      </header>

      <section className="relative py-16 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6">
            AI Revenue Intelligence:{" "}
            <span className="gradient-text">Forecast, Diagnose, and Accelerate Growth</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Gain real-time visibility into your pipeline with AI-powered forecasting, 
            deal risk identification, and conversion probability models.
          </p>
          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Start Free Trial<ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <article key={feature.title} className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10"><feature.icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-semibold">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
